import type { AccountProps } from 'app-components/flux/accounts/accountsTypes';
import { categoriesSelectors } from 'app-components/flux/categories';
import { chartOfAccountsSelectors } from 'app-components/flux/chart-of-accounts';
import type { ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { isAutoExcludedFromF2S } from 'app-components/flux/transactions/transactionsUtils';

type CategoryState = Parameters<typeof categoriesSelectors.getIncomeCOAIds>[0];

type ApplySeriesCategoryF2SExclusionArgs = {
  newCoa: ChartOfAccountProps | null | undefined;
  previousCoa: ChartOfAccountProps | null | undefined;
  account: AccountProps | null | undefined;
  state: CategoryState;
  create: boolean;
  excludeIncomeByDefault: boolean;
  setFieldValue: (path: string, value: unknown) => unknown;
};

export function shouldKeepManualIncomeIncludedOnSubmit({
  create,
  isIncome,
  excludeIncomeByDefault,
  excludeF2SUserOverridden,
  accountExcludedFromBudgets,
}: {
  create: boolean;
  isIncome: boolean;
  excludeIncomeByDefault: boolean;
  excludeF2SUserOverridden: boolean;
  accountExcludedFromBudgets: boolean;
}): boolean {
  return create && isIncome && excludeIncomeByDefault && !excludeF2SUserOverridden && !accountExcludedFromBudgets;
}

export function getSeriesCategoryFieldError({ error, touched }: { error: unknown; touched: unknown }): {
  error: boolean;
  helperText: string;
} {
  const helperText = touched && typeof error === 'string' ? error : '';

  return {
    error: helperText.length > 0,
    helperText,
  };
}

export function applySeriesCategoryF2SExclusion({
  newCoa,
  previousCoa,
  account,
  state,
  create,
  excludeIncomeByDefault,
  setFieldValue,
}: ApplySeriesCategoryF2SExclusionArgs): void {
  const incomeCoaIds = categoriesSelectors.getIncomeCOAIds(state);
  const businessCoaIds = categoriesSelectors.getBusinessCoaIdsSet(state);
  const shouldExcludeNewCoaFromF2S = isAutoExcludedFromF2S(
    newCoa,
    excludeIncomeByDefault,
    incomeCoaIds,
    businessCoaIds,
  );
  const didPreviousCoaAutoExcludeF2S = isAutoExcludedFromF2S(
    previousCoa,
    excludeIncomeByDefault,
    incomeCoaIds,
    businessCoaIds,
  );
  // The create-income flow defaults the series-level checkbox to included; changing
  // away from income and back should not silently re-check it.
  const shouldPreserveIncomeIncludeChoice =
    create && excludeIncomeByDefault && chartOfAccountsSelectors.isIncome(state, newCoa);

  if (shouldPreserveIncomeIncludeChoice && !account?.isExcludedFromBudgets) {
    setFieldValue('transaction.isExcludedFromF2S', false);
    setFieldValue('transaction.excludeF2SDest', false);
  } else if (shouldExcludeNewCoaFromF2S) {
    setFieldValue('transaction.isExcludedFromF2S', true);
    setFieldValue('transaction.excludeF2SDest', true);
  } else if (!account?.isExcludedFromBudgets && didPreviousCoaAutoExcludeF2S) {
    setFieldValue('transaction.isExcludedFromF2S', false);
    setFieldValue('transaction.excludeF2SDest', false);
  }
}
